import type { Instance } from "mobx-state-tree";
import { types, flow, getRoot, cast } from "mobx-state-tree";
import * as repo from "./repository";
import { BuildingDataAttribute } from "./models";
import type {
  BuildingDataAttributeRow,
  BuildingDataAttributeInsert,
  BuildingDataAttributeUpdate,
  BuildingDataAttributeSnapshotIn,
  AttributeSubClassRow,
  AttributeValidationRow,
  AttributeValidationInsert,
  AttributeValidationUpdate,
} from "./models";
import type { AdminRootAccess } from "../rootAccess";

export const BuildingDataAttributesStore = types
  .model("BuildingDataAttributesStore", {
    attributes: types.array(BuildingDataAttribute),
    // Cached sub-class IDs keyed by attribute_nm (filled by loadAllAttributeSubClasses)
    subClassesByAttribute: types.map(types.array(types.number)),
    // Cached validation rules keyed by attribute_nm (filled by loadAllAttributeValidations)
    validationsByAttribute: types.map(types.array(types.frozen<AttributeValidationRow>())),
    isLoading: types.optional(types.boolean, false),
    isLoadingRelationships: types.optional(types.boolean, false),
    error: types.maybeNull(types.string),
  })
  .views((self) => ({
    get attributesList() {
      return self.attributes.slice();
    },
    getByName(name: string) {
      return self.attributes.find((attr) => attr.attribute_nm === name);
    },
    get attributesByGroup() {
      const grouped = new Map<number, BuildingDataAttributeRow[]>();
      self.attributes.forEach((attr) => {
        const group = grouped.get(attr.group_id) || [];
        group.push(attr as BuildingDataAttributeRow);
        grouped.set(attr.group_id, group);
      });
      return grouped;
    },
    getSubClassIdsFor(attributeName: string): number[] {
      const entry = self.subClassesByAttribute.get(attributeName);
      return entry ? entry.slice() : [];
    },
    getValidationsFor(attributeName: string): AttributeValidationRow[] {
      const entry = self.validationsByAttribute.get(attributeName);
      return entry ? entry.slice() : [];
    },
    getValidationCountFor(attributeName: string): number {
      return self.validationsByAttribute.get(attributeName)?.length ?? 0;
    },
  }))
  .actions((self) => ({
    loadAttributes: flow(function* loadAttributes() {
      self.isLoading = true;
      self.error = null;

      try {
        const data: BuildingDataAttributeRow[] = yield repo.listAttributes();
        self.attributes.clear();
        data.forEach((attr) => {
          self.attributes.push(attr as BuildingDataAttributeSnapshotIn);
        });
      } catch (e) {
        self.error = e instanceof Error ? e.message : String(e);
        throw e;
      } finally {
        self.isLoading = false;
      }
    }),

    createAttribute: flow(function* createAttribute(input: BuildingDataAttributeInsert) {
      self.error = null;

      try {
        const created: BuildingDataAttributeRow = yield repo.createAttribute(input);
        self.attributes.push(created as BuildingDataAttributeSnapshotIn);
        return created;
      } catch (e) {
        self.error = e instanceof Error ? e.message : String(e);
        throw e;
      }
    }),

    updateAttribute: flow(function* updateAttribute(
      attributeName: string,
      updates: BuildingDataAttributeUpdate
    ) {
      self.error = null;

      try {
        const updated: BuildingDataAttributeRow = yield repo.updateAttribute(
          attributeName,
          updates
        );

        const index = self.attributes.findIndex((a) => a.attribute_nm === attributeName);
        if (index !== -1) {
          self.attributes[index] = cast(updated as BuildingDataAttributeSnapshotIn);
        }

        return updated;
      } catch (e) {
        self.error = e instanceof Error ? e.message : String(e);
        throw e;
      }
    }),

    deleteAttribute: flow(function* deleteAttribute(attributeName: string) {
      self.error = null;

      try {
        yield repo.deleteAttribute(attributeName);

        const index = self.attributes.findIndex((a) => a.attribute_nm === attributeName);
        if (index !== -1) {
          self.attributes.splice(index, 1);
        }
      } catch (e) {
        self.error = e instanceof Error ? e.message : String(e);
        throw e;
      }
    }),

    // M2M Relationship Methods

    loadAttributeCategories: flow(function* loadAttributeCategories(attributeName: string) {
      self.error = null;
      try {
        const categoryIds: number[] = yield repo.getAttributeCategories(attributeName);
        return categoryIds;
      } catch (e) {
        self.error = e instanceof Error ? e.message : String(e);
        throw e;
      }
    }),

    setAttributeCategories: flow(function* setAttributeCategories(
      attributeName: string,
      categoryIds: number[]
    ) {
      self.error = null;
      try {
        const root = getRoot<AdminRootAccess>(self);
        const userId = root.authStore.userProfileId;
        if (!userId) {
          throw new Error("User must be authenticated to update attribute relationships");
        }
        yield repo.setAttributeCategories(attributeName, categoryIds, userId);
        // Invalidate global M2M cache so other components refresh
        root.invalidateM2MCache();
      } catch (e) {
        self.error = e instanceof Error ? e.message : String(e);
        throw e;
      }
    }),

    loadAttributeSubClasses: flow(function* loadAttributeSubClasses(attributeName: string) {
      self.error = null;
      try {
        const subClassIds: number[] = yield repo.getAttributeSubClasses(attributeName);
        return subClassIds;
      } catch (e) {
        self.error = e instanceof Error ? e.message : String(e);
        throw e;
      }
    }),

    setAttributeSubClasses: flow(function* setAttributeSubClasses(
      attributeName: string,
      subClassIds: number[]
    ) {
      self.error = null;
      try {
        yield repo.setAttributeSubClasses(attributeName, subClassIds);
        self.subClassesByAttribute.set(attributeName, subClassIds);
        const root = getRoot<AdminRootAccess>(self);
        root.invalidateM2MCache();
      } catch (e) {
        self.error = e instanceof Error ? e.message : String(e);
        throw e;
      }
    }),

    // Bulk loader for attribute → sub-class relationships. Replaces the ~179
    // parallel getAttributeSubClasses calls previously made by AttributesTable.
    loadAllAttributeSubClasses: flow(function* loadAllAttributeSubClasses() {
      self.error = null;
      try {
        const rows: AttributeSubClassRow[] = yield repo.listAllAttributeSubClasses();
        const grouped = new Map<string, number[]>();
        rows.forEach((row) => {
          const bucket = grouped.get(row.attribute_nm) ?? [];
          bucket.push(row.sub_class_id);
          grouped.set(row.attribute_nm, bucket);
        });
        self.subClassesByAttribute.clear();
        grouped.forEach((ids, name) => {
          self.subClassesByAttribute.set(name, ids);
        });
      } catch (e) {
        self.error = e instanceof Error ? e.message : String(e);
        throw e;
      }
    }),

    // Bulk loader for validation rules. Buckets all rules by attribute_nm.
    loadAllAttributeValidations: flow(function* loadAllAttributeValidations() {
      self.error = null;
      try {
        const rows: AttributeValidationRow[] = yield repo.listAttributeValidations();
        const grouped = new Map<string, AttributeValidationRow[]>();
        rows.forEach((row) => {
          const bucket = grouped.get(row.attribute_nm) ?? [];
          bucket.push(row);
          grouped.set(row.attribute_nm, bucket);
        });
        self.validationsByAttribute.clear();
        grouped.forEach((rules, name) => {
          self.validationsByAttribute.set(name, rules);
        });
      } catch (e) {
        self.error = e instanceof Error ? e.message : String(e);
        throw e;
      }
    }),

    loadValidationsForAttribute: flow(function* loadValidationsForAttribute(attributeName: string) {
      self.error = null;
      try {
        const rows: AttributeValidationRow[] = yield repo.listValidationsByAttribute(attributeName);
        self.validationsByAttribute.set(attributeName, rows);
        return rows;
      } catch (e) {
        self.error = e instanceof Error ? e.message : String(e);
        throw e;
      }
    }),

    createAttributeValidation: flow(function* createAttributeValidation(
      input: AttributeValidationInsert
    ) {
      self.error = null;
      try {
        const created: AttributeValidationRow = yield repo.createAttributeValidation(input);
        const existing = self.validationsByAttribute.get(created.attribute_nm) ?? [];
        self.validationsByAttribute.set(created.attribute_nm, [...existing, created]);
        const root = getRoot<AdminRootAccess>(self);
        root.invalidateM2MCache();
        return created;
      } catch (e) {
        self.error = e instanceof Error ? e.message : String(e);
        throw e;
      }
    }),

    updateAttributeValidation: flow(function* updateAttributeValidation(
      id: number,
      updates: AttributeValidationUpdate
    ) {
      self.error = null;
      try {
        const updated: AttributeValidationRow = yield repo.updateAttributeValidation(id, updates);
        const bucket = self.validationsByAttribute.get(updated.attribute_nm) ?? [];
        const idx = bucket.findIndex((r) => r.id === id);
        if (idx !== -1) {
          const next = [...bucket];
          next[idx] = updated;
          self.validationsByAttribute.set(updated.attribute_nm, next);
        }
        const root = getRoot<AdminRootAccess>(self);
        root.invalidateM2MCache();
        return updated;
      } catch (e) {
        self.error = e instanceof Error ? e.message : String(e);
        throw e;
      }
    }),

    deleteAttributeValidation: flow(function* deleteAttributeValidation(
      id: number,
      attributeName: string
    ) {
      self.error = null;
      try {
        yield repo.deleteAttributeValidation(id);
        const bucket = self.validationsByAttribute.get(attributeName);
        if (bucket) {
          self.validationsByAttribute.set(
            attributeName,
            bucket.filter((r) => r.id !== id)
          );
        }
        const root = getRoot<AdminRootAccess>(self);
        root.invalidateM2MCache();
      } catch (e) {
        self.error = e instanceof Error ? e.message : String(e);
        throw e;
      }
    }),

    clear() {
      self.attributes.clear();
      self.subClassesByAttribute.clear();
      self.validationsByAttribute.clear();
      self.isLoading = false;
      self.isLoadingRelationships = false;
      self.error = null;
    },
  }));

export type BuildingDataAttributesStoreInstance = Instance<typeof BuildingDataAttributesStore>;
